import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import PageHeader from '../components/PageHeader';
import NutritionTargetsPanel from '../components/NutritionTargetsPanel';
import NutritionWhyPanel from '../components/NutritionWhyPanel';
import PillarTrendChart from '../components/PillarTrendChart';
import DistrictEntryButton from '../components/district/DistrictEntryButton';
import ScoreBreakdownPopover from '../components/ScoreBreakdownPopover';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Camera, Utensils, Plus, Trash2, Edit3, X, Check, Sparkles, Settings, Loader2, Droplet, Wine, Minus } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import nutritionApi from '../services/nutritionApi';
import MacroRing from '../components/nutrition/MacroRing';
import WeekStackedChart from '../components/nutrition/WeekStackedChart';

const MEAL_TYPES = ['breakfast', 'lunch', 'dinner', 'snack', 'preworkout', 'postworkout'];

const getScoreColor = (s) => s >= 85 ? '#a3e635' : s >= 65 ? '#CCFF00' : s >= 45 ? '#facc15' : s >= 25 ? '#fb923c' : '#f87171';

/** Water + alcohol tracker — litres-based hydration (editable goal, +250ml taps)
 *  and an alcohol counter whose coaching line + water goal scale per drink. */
const alcoholHint = (n) => {
  if (n <= 0) return 'tap if you drink';
  if (n === 1) return 'one drink — match it with a glass of water 💧';
  if (n === 2) return 'two in — add ~500ml water + a protein-forward next meal';
  if (n === 3) return 'three — keep water flowing tonight, recovery will thank you';
  return 'big one — front-load water now & go gentle on training tomorrow';
};

const IntakeTracker = ({ intake, onIntake, onSetWaterTarget }) => {
  const glasses = intake?.water_glasses ?? 0;
  const baseGlasses = intake?.water_target_glasses ?? intake?.water_target ?? 12;
  const effGlasses = intake?.water_target_effective ?? baseGlasses;
  const bonusGlasses = intake?.water_bonus_from_alcohol ?? 0;
  const alcohol = intake?.alcohol_drinks ?? 0;

  const [editing, setEditing] = useState(false);
  const [goalL, setGoalL] = useState((baseGlasses * 0.25).toFixed(1));
  useEffect(() => { setGoalL((baseGlasses * 0.25).toFixed(1)); }, [baseGlasses]);

  const waterL = glasses * 0.25;
  const effL = effGlasses * 0.25;

  const saveGoal = () => {
    const ml = Math.round(parseFloat(goalL) * 1000);
    if (ml >= 1000 && ml <= 6000) onSetWaterTarget?.(ml);
    setEditing(false);
  };

  const StepButtons = ({ kind, value }) => (
    <div className="flex items-center gap-1.5 shrink-0">
      <button onClick={() => onIntake(kind, -1)} disabled={value <= 0}
        className="w-7 h-7 rounded-sm border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 disabled:opacity-30 flex items-center justify-center transition-colors"
        data-testid={`intake-${kind}-minus`}><Minus className="w-3.5 h-3.5" /></button>
      <button onClick={() => onIntake(kind, 1)}
        className="w-7 h-7 rounded-sm text-black flex items-center justify-center transition-transform hover:scale-105"
        style={{ background: kind === 'water' ? '#22d3ee' : '#fb923c' }}
        data-testid={`intake-${kind}-plus`}><Plus className="w-3.5 h-3.5" /></button>
    </div>
  );

  return (
    <div className="mt-5 pt-4 border-t border-zinc-800" data-testid="intake-tracker">
      <div className="flex items-center justify-between mb-1">
        <p className="font-secondary text-[10px] uppercase tracking-[0.2em] text-zinc-500">Hydration &amp; balance</p>
        <button onClick={() => setEditing(e => !e)} data-testid="water-goal-edit-btn"
          className="text-[10px] uppercase tracking-wider text-zinc-500 hover:text-cyan-400 flex items-center gap-1 transition-colors">
          <Settings className="w-3 h-3" /> Goal
        </button>
      </div>

      {editing && (
        <div className="flex items-center gap-2 mb-2 p-2 rounded-sm bg-zinc-950/60 border border-zinc-800" data-testid="water-goal-editor">
          <input type="number" step="0.5" min="1" max="6" value={goalL}
            onChange={e => setGoalL(e.target.value)}
            className="hpme-input w-20 px-2 py-1 text-sm" data-testid="water-goal-input" />
          <span className="text-xs text-zinc-500 flex-1">litres / day</span>
          <button onClick={saveGoal} className="text-xs font-bold text-black bg-cyan-400 hover:bg-cyan-300 rounded-sm px-3 py-1 transition-colors" data-testid="water-goal-save">Save</button>
        </div>
      )}

      {/* Water */}
      <div className="flex items-center justify-between gap-3 py-2" data-testid="intake-row-water">
        <div className="flex items-center gap-2 min-w-0">
          <Droplet className="w-4 h-4 shrink-0" style={{ color: '#22d3ee' }} />
          <div className="min-w-0">
            <p className="text-sm font-secondary font-bold" style={{ color: '#22d3ee' }} data-testid="intake-water-value">
              {waterL.toFixed(2)} L <span className="text-zinc-500 font-normal">/ {effL.toFixed(1)} L</span>
            </p>
            <p className="text-[10px] text-zinc-500 leading-snug">
              water · +250ml per tap{bonusGlasses > 0 ? ` · +${bonusGlasses * 250}ml from alcohol` : ''}
            </p>
          </div>
        </div>
        <StepButtons kind="water" value={glasses} />
      </div>

      {/* Alcohol */}
      <div className="flex items-center justify-between gap-3 py-2" data-testid="intake-row-alcohol">
        <div className="flex items-center gap-2 min-w-0">
          <Wine className="w-4 h-4 shrink-0" style={{ color: '#fb923c' }} />
          <div className="min-w-0">
            <p className="text-sm font-secondary font-bold" style={{ color: '#fb923c' }} data-testid="intake-alcohol-value">
              {alcohol} <span className="text-zinc-500 font-normal text-xs">{alcohol === 1 ? 'drink' : 'drinks'} today</span>
            </p>
            <p className="text-[10px] text-zinc-500 leading-snug" data-testid="intake-alcohol-hint">{alcoholHint(alcohol)}</p>
          </div>
        </div>
        <StepButtons kind="alcohol" value={alcohol} />
      </div>
    </div>
  );
};

const Nutrition = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [today, setToday] = useState(null);
  const [week, setWeek] = useState(null);
  const [targets, setTargets] = useState(null);
  const [iq, setIq] = useState(null);
  const [loading, setLoading] = useState(true);

  const [text, setText] = useState('');
  const [mealType, setMealType] = useState('snack');
  const [parsing, setParsing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);

  const [showTargets, setShowTargets] = useState(false);
  const [targetForm, setTargetForm] = useState({});

  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, []);

  const loadIq = useCallback(() => {
    api.get('/wellness/iq').then(r => setIq(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadIq();
    const onRefresh = () => loadIq();
    window.addEventListener('wellness:refresh', onRefresh);
    return () => window.removeEventListener('wellness:refresh', onRefresh);
  }, [loadIq]);

  const handleIntake = async (kind, delta) => {
    try {
      const r = await nutritionApi.intake(kind, delta);
      setToday(r.data.today);
      loadIq();
      window.dispatchEvent(new Event('wellness:refresh'));
    } catch { toast.error('Could not update'); }
  };

  const handleSetWaterTarget = async (ml) => {
    try {
      const r = await nutritionApi.setWaterTarget(ml);
      setToday(r.data.today);
      loadIq();
      window.dispatchEvent(new Event('wellness:refresh'));
      toast.success('Water goal updated');
    } catch { toast.error('Could not update goal'); }
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const [t, w, tg] = await Promise.all([
        nutritionApi.today(), nutritionApi.week(), nutritionApi.getTargets(),
      ]);
      setToday(t.data); setWeek(w.data); setTargets(tg.data);
      loadIq();
    } catch { toast.error('Could not load nutrition data'); }
    finally { setLoading(false); }
  };

  const handleParseText = async () => {
    if (!text.trim()) { toast.error('Type what you ate first'); return; }
    setParsing(true);
    try {
      const r = await nutritionApi.parseText(text);
      if (!r.data.items?.length) { toast.error("Couldn't parse anything — try adding more detail"); return; }
      setDraft({ ...r.data, source: 'text', raw_text: text });
    } catch { toast.error('Parse failed — try again'); }
    finally { setParsing(false); }
  };

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Photo too large (max 5MB)'); return; }
    setParsing(true);
    try {
      const r = await nutritionApi.parsePhoto(file);
      if (!r.data.items?.length) { toast.error("I couldn't identify foods in that photo — try another angle"); return; }
      setDraft({ ...r.data, source: 'photo' });
    } catch { toast.error('Photo analysis failed'); }
    finally { setParsing(false); }
  };

  const handleSave = async () => {
    if (!draft?.items?.length) return;
    setSaving(true);
    try {
      await nutritionApi.log({
        items: draft.items, raw_text: draft.raw_text || null,
        meal_type: mealType, source: draft.source,
        photo_path: draft.photo_path || null, notes: draft.notes || null,
      });
      toast.success('Meal logged');
      setText(''); setDraft(null);
      await refresh();
    } catch { toast.error('Could not save'); }
    finally { setSaving(false); }
  };

  const updateDraftItem = (idx, key, value) => {
    setDraft(d => {
      const items = [...d.items];
      const numeric = ['kcal', 'protein_g', 'carb_g', 'fat_g'];
      items[idx] = { ...items[idx], [key]: numeric.includes(key) ? Number(value) || 0 : value };
      return { ...d, items };
    });
  };
  const removeDraftItem = (idx) => setDraft(d => {
    const items = d.items.filter((_, i) => i !== idx);
    return items.length ? { ...d, items } : null;
  });
  const addDraftItem = () => setDraft(d => ({
    ...d,
    items: [...(d?.items || []), { name: '', quantity: '', kcal: 0, protein_g: 0, carb_g: 0, fat_g: 0, confidence: 'medium' }],
  }));

  // ---------- Edit existing meal ----------
  const startEdit = (log) => {
    setEditingId(log.id);
    setEditDraft({ items: log.items.map(i => ({ ...i })), meal_type: log.meal_type || 'snack' });
  };
  const updateEditItem = (idx, key, value) => {
    setEditDraft(d => {
      const items = [...d.items];
      const numeric = ['kcal', 'protein_g', 'carb_g', 'fat_g'];
      items[idx] = { ...items[idx], [key]: numeric.includes(key) ? Number(value) || 0 : value };
      return { ...d, items };
    });
  };
  const removeEditItem = (idx) => setEditDraft(d => {
    const items = d.items.filter((_, i) => i !== idx);
    return { ...d, items };
  });
  const addEditItem = () => setEditDraft(d => ({
    ...d, items: [...d.items, { name: '', quantity: '', kcal: 0, protein_g: 0, carb_g: 0, fat_g: 0, confidence: 'medium' }],
  }));
  const saveEdit = async () => {
    if (!editingId || !editDraft) return;
    try {
      await nutritionApi.patchLog(editingId, { items: editDraft.items, meal_type: editDraft.meal_type });
      toast.success('Updated');
      setEditingId(null); setEditDraft(null);
      refresh();
    } catch { toast.error('Update failed'); }
  };

  const handleDeleteLog = async (id) => {
    if (!window.confirm('Delete this meal?')) return;
    try { await nutritionApi.deleteLog(id); toast.success('Deleted'); refresh(); }
    catch { toast.error('Delete failed'); }
  };

  const handleSaveTargets = async () => {
    try {
      await nutritionApi.patchTargets(targetForm);
      toast.success('Targets updated');
      setShowTargets(false); setTargetForm({});
      refresh();
    } catch { toast.error('Could not save targets'); }
  };

  const handleSuggest = async (mt) => {
    setSuggestLoading(true); setSuggestions([]);
    try {
      const r = await nutritionApi.suggest(mt);
      setSuggestions(r.data.suggestions || []);
      if (!(r.data.suggestions || []).length) {
        toast.error('No suggestions right now — try filling in your weight + goal first');
      }
    } catch { toast.error('Suggestions failed'); }
    finally { setSuggestLoading(false); }
  };

  if (loading) {
    const sk = (<div className="min-h-[40vh] flex items-center justify-center"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>);
    return embedded ? sk : <DashboardLayout>{sk}</DashboardLayout>;
  }

  const totals = today?.totals || {};
  const activeTargets = targets?.active || today?.targets || null;

  const body = (
      <div className={embedded ? 'max-w-2xl mx-auto px-5 py-8 space-y-8 living-skin' : 'px-4 sm:px-6 py-6 max-w-6xl mx-auto space-y-8'} style={embedded ? { '--skin-rgb': '251,146,60' } : undefined}>
        {/* Header */}
        {!embedded && (
        <PageHeader
          tag="Nutrition Intelligence"
          tagColor="#fb923c"
          icon={Utensils}
          title="Nutrition"
          subtitle="Track meals, hit your macros, dial in your fuel."
          testIdPrefix="nutrition"
          actions={(
            <div className="flex items-center gap-3">
              {iq?.sub_scores?.nutrition != null && (
                <div className="flex items-center gap-2">
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-secondary">Nutrition IQ</p>
                    <p className="font-secondary font-bold text-3xl" style={{ color: getScoreColor(iq.sub_scores.nutrition) }} data-testid="nutrition-iq-score">{iq.sub_scores.nutrition}</p>
                  </div>
                  <ScoreBreakdownPopover
                    breakdown={iq?.score_breakdown?.nutrition}
                    label="Nutrition formula"
                    finalScore={iq.sub_scores.nutrition}
                    color="#fb923c"
                    testId="nutrition-breakdown"
                  />
                </div>
              )}
              <Button
                variant="outline"
                onClick={() => { setShowTargets(s => !s); setTargetForm(targets?.override || {}); }}
                data-testid="nutrition-targets-btn"
                className="border-zinc-700 hover:border-primary hover:text-primary transition-colors"
              >
                <Settings className="w-4 h-4 mr-2" /> Targets
              </Button>
            </div>
          )}
        />
        )}

        {/* Synthesized WHY — fueling drivers. Hidden in the embedded world to
            avoid repeating Nikki's "why this world looks like this" read above. */}
        {!embedded && (
        <NutritionWhyPanel
          insight={iq?.nutrition_insight}
          score={iq?.sub_scores?.nutrition}
          onActionClick={() => navigate('/chat')}
        />
        )}

        <PillarTrendChart pillar="nutrition" color="#fb923c" label="Nutrition" />

        {!embedded && <DistrictEntryButton pillar="nutrition" accent="#FBBF24" name="Nutrition" />}

        <NutritionTargetsPanel />

        {/* Targets override */}
        {showTargets && (
          <Card className="border-zinc-800 bg-zinc-900/50" data-testid="nutrition-targets-panel">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-secondary text-lg font-bold uppercase tracking-wider">Macro targets</h3>
                <button onClick={() => setShowTargets(false)} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              {targets?.missing_for_compute?.length > 0 && (
                <p className="text-sm text-amber-400">
                  Missing <strong>{targets.missing_for_compute.join(', ')}</strong> in your profile —
                  the auto-compute won't run. Set targets manually below.
                </p>
              )}
              {targets?.computed && (
                <p className="text-sm text-zinc-400">
                  Auto-computed: {targets.computed.daily_kcal_target} kcal, {targets.computed.protein_target_g}g P,
                  {' '}{targets.computed.carb_target_g}g C, {targets.computed.fat_target_g}g F
                </p>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  ['daily_kcal_target', 'kcal'],
                  ['protein_target_g', 'Protein g'],
                  ['carb_target_g', 'Carb g'],
                  ['fat_target_g', 'Fat g'],
                ].map(([k, label]) => (
                  <div key={k}>
                    <label className="text-xs text-zinc-500 uppercase">{label}</label>
                    <Input type="number" data-testid={`target-input-${k}`}
                      value={targetForm[k] ?? targets?.override?.[k] ?? ''}
                      onChange={e => setTargetForm(f => ({ ...f, [k]: parseInt(e.target.value || '0') }))} />
                  </div>
                ))}
              </div>
              <Button onClick={handleSaveTargets} className="bg-primary text-black font-bold" data-testid="save-targets-btn">
                <Check className="w-4 h-4 mr-2" /> Save targets
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Today + Log */}
        <section className="grid grid-cols-1 lg:grid-cols-[420px,1fr] gap-6 items-start">
          <Card className="border-zinc-800 bg-zinc-900/50 hover:border-primary/40 transition-colors">
            <CardContent className="p-6">
              <h2 className="font-secondary text-xs uppercase tracking-[0.2em] text-primary mb-4">Today</h2>
              <MacroRing totals={totals} targets={activeTargets} />
              <IntakeTracker intake={today?.intake} onIntake={handleIntake} onSetWaterTarget={handleSetWaterTarget} />
            </CardContent>
          </Card>

          <Card className="border-zinc-800 bg-zinc-900/50" data-testid="nutrition-log-card">
            <CardContent className="p-6 space-y-4">
              <h2 className="font-secondary text-xs uppercase tracking-[0.2em] text-primary">Log a meal</h2>
              {!draft ? (
                <>
                  <Textarea rows={2} placeholder='e.g. "2 rotis + dal + 1 cup brown rice + salad"'
                    value={text} onChange={e => setText(e.target.value)}
                    className="bg-zinc-950 border-zinc-800 focus:border-primary transition-colors"
                    data-testid="nutrition-text-input" />
                  <div className="flex items-center gap-2 flex-wrap">
                    <select value={mealType} onChange={e => setMealType(e.target.value)}
                      className="bg-zinc-950 border border-zinc-800 hover:border-zinc-600 rounded px-3 py-2 text-sm transition-colors cursor-pointer"
                      data-testid="meal-type-select">
                      {MEAL_TYPES.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <Button onClick={handleParseText} disabled={parsing || !text.trim()}
                      className="bg-primary text-black font-bold hover:bg-primary/90 transition-all"
                      data-testid="parse-text-btn">
                      {parsing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                      Log it
                    </Button>
                    <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={parsing}
                      className="border-zinc-700 hover:border-primary hover:text-primary transition-colors"
                      data-testid="parse-photo-btn">
                      <Camera className="w-4 h-4 mr-2" /> Photo
                    </Button>
                    <input ref={fileInputRef} type="file"
                      accept="image/jpeg,image/png,image/webp" capture="environment"
                      onChange={handlePhotoSelect} className="hidden"
                      data-testid="photo-file-input" />
                  </div>
                </>
              ) : (
                <DraftEditor
                  draft={draft} mealType={mealType} setMealType={setMealType}
                  onUpdate={updateDraftItem} onAdd={addDraftItem} onRemove={removeDraftItem}
                  onCancel={() => setDraft(null)} onSave={handleSave} saving={saving}
                />
              )}
            </CardContent>
          </Card>
        </section>

        {/* Today's meals */}
        <section data-testid="todays-meals-section">
          <h2 className="font-secondary text-xs uppercase tracking-[0.2em] text-primary mb-3">Today's meals</h2>
          {(today?.logs || []).length === 0 ? (
            <p className="text-zinc-500 italic">Nothing logged yet today.</p>
          ) : (
            <div className="space-y-3">
              {today.logs.map(log => (
                editingId === log.id ? (
                  <Card key={log.id} className="border-primary/40 bg-zinc-900/60" data-testid={`meal-edit-${log.id}`}>
                    <CardContent className="p-4">
                      <DraftEditor
                        draft={editDraft} mealType={editDraft.meal_type}
                        setMealType={mt => setEditDraft(d => ({ ...d, meal_type: mt }))}
                        onUpdate={updateEditItem} onAdd={addEditItem} onRemove={removeEditItem}
                        onCancel={() => { setEditingId(null); setEditDraft(null); }}
                        onSave={saveEdit} saving={false} saveLabel="Save changes"
                      />
                    </CardContent>
                  </Card>
                ) : (
                  <MealRow key={log.id} log={log}
                    onEdit={() => startEdit(log)}
                    onDelete={() => handleDeleteLog(log.id)} />
                )
              ))}
            </div>
          )}
        </section>

        {/* Week stacked */}
        {week?.days && (
          <section data-testid="week-section">
            <h2 className="font-secondary text-xs uppercase tracking-[0.2em] text-primary mb-3">Last 7 days</h2>
            <Card className="border-zinc-800 bg-zinc-900/50">
              <CardContent className="p-5">
                <WeekStackedChart days={week.days} target={activeTargets?.daily_kcal_target} />
              </CardContent>
            </Card>
          </section>
        )}

        {/* AI suggestions */}
        <section data-testid="suggestions-section">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="font-secondary text-xs uppercase tracking-[0.2em] text-primary">Meal ideas for you</h2>
            <div className="flex gap-2">
              {['breakfast', 'lunch', 'dinner', 'snack'].map(mt => (
                <Button key={mt} size="sm" variant="outline"
                  onClick={() => handleSuggest(mt)} disabled={suggestLoading}
                  className="border-zinc-700 hover:border-primary hover:text-primary transition-colors"
                  data-testid={`suggest-${mt}-btn`}>
                  {suggestLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : mt}
                </Button>
              ))}
            </div>
          </div>
          {suggestions.length === 0 ? (
            <p className="text-zinc-500 italic">Tap a meal type for ideas tailored to your diet, goal, and what's left for today.</p>
          ) : (
            <div className="grid sm:grid-cols-3 gap-3">
              {suggestions.map((s, i) => (
                <Card key={i}
                  className="border-zinc-800 bg-zinc-900/50 hover:border-primary/50 hover:bg-zinc-900/70 transition-all cursor-pointer"
                  data-testid={`suggestion-${i}`}>
                  <CardContent className="p-4 space-y-2">
                    <h4 className="font-semibold">{s.name}</h4>
                    <p className="text-xs text-zinc-400">{s.why}</p>
                    <div className="text-xs text-zinc-500">
                      {s.kcal} kcal · P {s.protein_g}g · C {s.carb_g}g · F {s.fat_g}g
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>
      </div>
  );
  return embedded ? body : <DashboardLayout>{body}</DashboardLayout>;
};

// ---------- Sub-components ----------

const DraftEditor = ({ draft, mealType, setMealType, onUpdate, onAdd, onRemove, onCancel, onSave, saving, saveLabel = 'Save meal' }) => {
  const sum = (k) => draft.items.reduce((acc, it) => acc + (Number(it[k]) || 0), 0).toFixed(1);
  return (
    <div className="space-y-3" data-testid="draft-editor">
      <div className="flex items-center justify-between">
        <select value={mealType} onChange={e => setMealType(e.target.value)}
          className="bg-zinc-950 border border-zinc-800 hover:border-zinc-600 rounded px-3 py-1.5 text-sm transition-colors cursor-pointer"
          data-testid="draft-meal-type">
          {MEAL_TYPES.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        {draft.notes && <span className="text-xs text-zinc-500 italic">{draft.notes}</span>}
      </div>
      <div className="space-y-2">
        {draft.items.map((it, idx) => (
          <div key={idx} className="bg-zinc-950 border border-zinc-800 rounded p-2 hover:border-zinc-700 transition-colors space-y-2" data-testid={`draft-item-${idx}`}>
            {/* Name + qty + remove */}
            <div className="flex items-center gap-2">
              <Input className="flex-1 min-w-0" value={it.name} onChange={e => onUpdate(idx, 'name', e.target.value)} placeholder="name" data-testid={`draft-item-name-${idx}`} />
              <Input className="w-24 flex-shrink-0" value={it.quantity || ''} onChange={e => onUpdate(idx, 'quantity', e.target.value)} placeholder="qty" data-testid={`draft-item-qty-${idx}`} />
              <button onClick={() => onRemove(idx)} className="text-red-400 hover:text-red-300 flex items-center justify-center p-1.5 flex-shrink-0 transition-colors" data-testid={`remove-item-${idx}`} title="Remove item">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            {/* Macros — labeled grid so values stay readable/editable on mobile */}
            <div className="grid grid-cols-4 gap-2">
              {[
                { key: 'kcal', label: 'kcal', color: '#ffffff' },
                { key: 'protein_g', label: 'Protein (g)', color: '#f97316' },
                { key: 'carb_g', label: 'Carbs (g)', color: '#3b82f6' },
                { key: 'fat_g', label: 'Fat (g)', color: '#a855f7' },
              ].map(f => (
                <div key={f.key} className="space-y-1">
                  <label className="block text-[10px] uppercase tracking-wider" style={{ color: f.color }}>{f.label}</label>
                  <Input type="number" inputMode="decimal" className="w-full text-center px-1" value={it[f.key]} onChange={e => onUpdate(idx, f.key, e.target.value)} placeholder="0" data-testid={`draft-item-${f.key}-${idx}`} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="text-sm text-zinc-400">
        Total: <strong className="text-white">{sum('kcal')} kcal</strong> · <span style={{ color: '#f97316' }}>P {sum('protein_g')}g</span> · <span style={{ color: '#3b82f6' }}>C {sum('carb_g')}g</span> · <span style={{ color: '#a855f7' }}>F {sum('fat_g')}g</span>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="outline" size="sm" onClick={onAdd} className="border-zinc-700 hover:border-primary hover:text-primary" data-testid="add-item-btn">
          <Plus className="w-4 h-4 mr-1" /> Add item
        </Button>
        <Button variant="ghost" onClick={onCancel} data-testid="cancel-draft-btn">Cancel</Button>
        <Button onClick={onSave} disabled={saving} className="bg-primary text-black font-bold hover:bg-primary/90" data-testid="save-meal-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
          {saveLabel}
        </Button>
      </div>
    </div>
  );
};

const MealRow = ({ log, onEdit, onDelete }) => {
  const time = log.logged_at ? new Date(log.logged_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
  return (
    <div className="flex items-start gap-3 bg-zinc-900/40 border border-zinc-800 rounded p-3 hover:border-primary/40 hover:bg-zinc-900/70 transition-all group" data-testid={`meal-row-${log.id}`}>
      <div className="w-10 h-10 bg-zinc-800 rounded flex items-center justify-center flex-shrink-0 group-hover:bg-primary/10 transition-colors">
        <Utensils className="w-5 h-5 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs uppercase tracking-wider text-zinc-500">{log.meal_type || 'meal'}</span>
          <span className="text-xs text-zinc-500">· {time}</span>
          {log.source === 'photo' && <span className="text-xs px-1.5 py-0.5 bg-zinc-800 text-zinc-400 rounded">📷</span>}
        </div>
        <p className="text-sm text-zinc-200 truncate font-medium">
          {(log.items || []).map(i => i.name).join(', ')}
        </p>
        <p className="text-xs text-zinc-500 mt-1">
          <span className="text-white font-semibold">{Math.round(log.total_kcal || 0)} kcal</span>
          <span> · </span>
          <span style={{ color: '#f97316' }}>P {Math.round(log.total_protein_g || 0)}g</span>
          <span> · </span>
          <span style={{ color: '#3b82f6' }}>C {Math.round(log.total_carb_g || 0)}g</span>
          <span> · </span>
          <span style={{ color: '#a855f7' }}>F {Math.round(log.total_fat_g || 0)}g</span>
        </p>
      </div>
      <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
        <button onClick={onEdit} className="text-zinc-400 hover:text-primary p-1.5 rounded hover:bg-primary/10 transition-colors"
          title="Edit meal" data-testid={`edit-meal-${log.id}`}>
          <Edit3 className="w-4 h-4" />
        </button>
        <button onClick={onDelete} className="text-zinc-400 hover:text-red-400 p-1.5 rounded hover:bg-red-500/10 transition-colors"
          title="Delete meal" data-testid={`delete-meal-${log.id}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default Nutrition;
